import { tbtIdleSchedule } from '@bcf-vanilla-ts-v1-shared/misc/tbt-scheduler/tbt-scheduler';
import type { ReactiveController, ReactiveElement } from 'lit';

/**
 * When the browser defines (and therefore hydrates) a lazily loaded child element:
 * - `render` - as soon as its tag shows up in the host's render root (conditional branches),
 * - `viewport` - when it gets near the viewport,
 * - `idle` - on main-thread idle,
 * - `interaction` - on the first pointerdown / keydown / focusin inside it; a click that lands
 *   before hydration is replayed once the element is hydrated,
 * - `manual` - only through `defineLazyElement` (e.g. to swap a placeholder only once loaded).
 * The server always defines every declared element before rendering, so SSR output is unchanged.
 */
// Decision: see ADR-0039
export type LazyElementTrigger = 'render' | 'viewport' | 'idle' | 'interaction' | 'manual';

export type LazyElement = {
  tag: string;
  load: () => Promise<CustomElementConstructor>;
  on: LazyElementTrigger;
  /** Download + evaluate the module earlier than `on`, without defining (no hydration cost). */
  prefetch?: 'viewport' | 'idle';
};

const viewportMargin: string = '1000px';

const loads: Map<string, Promise<CustomElementConstructor>> = new Map();
const entriesByTag: Map<string, LazyElement> = new Map();

function loadOnce(entry: LazyElement): Promise<CustomElementConstructor> {
  let load: Promise<CustomElementConstructor> | undefined = loads.get(entry.tag);
  if (!load) {
    load = entry.load();
    // a failed chunk request must stay retryable on the next trigger
    load.catch(() => loads.delete(entry.tag));
    loads.set(entry.tag, load);
  }
  return load;
}

export async function defineLazyElement(entry: LazyElement): Promise<void> {
  const elementClass: CustomElementConstructor = await loadOnce(entry);
  if (!customElements.get(entry.tag)) {
    customElements.define(entry.tag, elementClass);
  }
}

function lazyElementsOf(element: unknown): LazyElement[] {
  return (element as { _lazyElements?: LazyElement[] })._lazyElements ?? [];
}

export function hasLazyElements(element: unknown): boolean {
  return lazyElementsOf(element).length > 0;
}

export async function defineLazyElementsOnServer(element: unknown): Promise<void> {
  await Promise.all(lazyElementsOf(element).map(defineLazyElement));
}

type ViewportJob = () => void;

const viewportJobs: Map<Element, ViewportJob[]> = new Map();
let viewportObserver: IntersectionObserver | undefined;

function onNearViewport(element: Element, job: ViewportJob): void {
  viewportObserver ??= new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          continue;
        }
        const jobs: ViewportJob[] = viewportJobs.get(entry.target) ?? [];
        stopWatchingViewport(entry.target);
        jobs.forEach((run: ViewportJob) => run());
      }
    },
    { rootMargin: viewportMargin }
  );
  const jobs: ViewportJob[] | undefined = viewportJobs.get(element);
  if (jobs) {
    jobs.push(job);
    return;
  }
  viewportJobs.set(element, [job]);
  viewportObserver.observe(element);
}

function stopWatchingViewport(element: Element): void {
  viewportJobs.delete(element);
  viewportObserver?.unobserve(element);
}

// Outermost first: an inner element cannot hydrate before its host removes `defer-hydration`.
function pendingLazyElements(path: EventTarget[]): LazyElement[] {
  const pending: LazyElement[] = [];
  for (const target of path) {
    const tag: string | undefined = (target as Element).localName;
    const entry: LazyElement | undefined = tag ? entriesByTag.get(tag) : undefined;
    if (entry && !customElements.get(tag)) {
      pending.unshift(entry);
    }
  }
  return pending;
}

async function defineInOrder(entries: LazyElement[]): Promise<void> {
  for (const entry of entries) {
    await defineLazyElement(entry);
  }
}

async function settleUpdates(path: EventTarget[]): Promise<void> {
  for (const target of [...path].reverse()) {
    if (target instanceof Element && 'updateComplete' in target) {
      await (target as ReactiveElement).updateComplete;
    }
  }
}

async function replayClick(path: EventTarget[], pending: LazyElement[]): Promise<void> {
  await defineInOrder(pending);
  await settleUpdates(path);
  // Hydration reuses the SSR nodes, so the original target is the node that now has the handler.
  const target: EventTarget | undefined = path[0];
  if (target instanceof HTMLElement && target.isConnected) {
    target.click();
  }
}

let interactionListening: boolean = false;

function listenForInteractions(): void {
  // no `document` in the node-environment component specs (lit dom-shim)
  if (interactionListening || typeof document === 'undefined') {
    return;
  }
  interactionListening = true;
  const startDefining = (event: Event): void => {
    const pending: LazyElement[] = pendingLazyElements(event.composedPath());
    if (pending.length > 0) {
      void defineInOrder(pending);
    }
  };
  for (const type of ['pointerdown', 'keydown', 'focusin']) {
    document.addEventListener(type, startDefining, { capture: true, passive: true });
  }
  document.addEventListener(
    'click',
    (event: MouseEvent) => {
      const path: EventTarget[] = event.composedPath();
      const pending: LazyElement[] = pendingLazyElements(path);
      if (pending.length === 0) {
        return;
      }
      // Nothing can handle the click yet; a link would navigate without the app's routing.
      event.preventDefault();
      event.stopImmediatePropagation();
      replayClick(path, pending);
    },
    { capture: true }
  );
}

// An undefined element without SSR markup (CSR render, or SSR output that renders only in the
// browser) is an empty box - nobody can see or tap it, so `interaction` would never fire.
function hasVisibleSsrContent(element: Element): boolean {
  return Array.from(element.shadowRoot?.children ?? []).some(
    (child: Element) =>
      !['style', 'link', 'script'].includes(child.localName) && (child as HTMLElement).style?.display !== 'none'
  );
}

export class LazyElementsController implements ReactiveController {
  private _scheduled: WeakSet<Element> = new WeakSet();
  private _watched: Set<Element> = new Set();

  constructor(
    private _host: ReactiveElement,
    private _entries: LazyElement[]
  ) {
    for (const entry of _entries) {
      entriesByTag.set(entry.tag, entry);
    }
    // Any pending lazy element can be tapped, e.g. a `viewport` parent of an `interaction` child
    // that is not yet defined and therefore has not declared that child yet.
    listenForInteractions();
    _host.addController(this);
  }

  public hostConnected(): void {
    if (this._host.hasUpdated) {
      this._scan();
    }
  }

  public hostUpdated(): void {
    this._scan();
  }

  public hostDisconnected(): void {
    this._watched.forEach(stopWatchingViewport);
    this._watched.clear();
    this._scheduled = new WeakSet();
  }

  private _scan(): void {
    for (const entry of this._entries) {
      if (entry.on === 'manual' || customElements.get(entry.tag)) {
        continue;
      }
      this._host.renderRoot.querySelectorAll(entry.tag).forEach((element: Element) => {
        if (!this._scheduled.has(element)) {
          this._scheduled.add(element);
          this._schedule(entry, element);
        }
      });
    }
  }

  private _schedule(entry: LazyElement, element: Element): void {
    const define = (): void => void defineLazyElement(entry);
    if (entry.on === 'render' || (entry.on === 'interaction' && !hasVisibleSsrContent(element))) {
      define();
      return;
    }
    if (entry.on === 'idle') {
      tbtIdleSchedule(define);
    } else if (entry.on === 'viewport') {
      this._watchViewport(element, define);
    }
    if (entry.prefetch === 'idle') {
      tbtIdleSchedule(() => void loadOnce(entry));
    } else if (entry.prefetch === 'viewport') {
      this._watchViewport(element, () => void loadOnce(entry));
    }
  }

  private _watchViewport(element: Element, job: ViewportJob): void {
    this._watched.add(element);
    onNearViewport(element, job);
  }
}
